import { useState } from "react";
import { Link } from "react-router-dom";
import { wholesaleStats, wholesaleProducts, wholesaleTiers } from "@/mocks/wholesale";

const WholesaleSection = () => {
  const [activeTab, setActiveTab] = useState<"products" | "tiers">("products");
  const [orderQty, setOrderQty] = useState<Record<string, number>>({});

  const updateQty = (id: string, val: number) => {
    setOrderQty((prev) => ({ ...prev, [id]: Math.max(1, val) }));
  };

  return (
    <section id="wholesale" className="py-20 bg-[#f8f7f5]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-[1400px] mx-auto px-6">

        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] text-xs font-bold px-4 py-2 rounded-full mb-4">
            <i className="ri-building-2-line"></i>
            USA FACTORY WHOLESALE
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
            Bulk Orders Direct from<br />
            <span className="text-[#C41E3A]">USA Factories & Warehouses</span>
          </h2>
          <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
            Order in bulk directly from verified USA factories and local warehouses. Minimum order <strong>$6,000 USD</strong> — unlock wholesale pricing up to 60% below retail. Fast 2–5 day delivery across all 50 states.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {wholesaleStats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-100 p-5 text-center">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C41E3A]/10 rounded-xl mx-auto mb-3">
                <i className={stat.icon + " text-[#C41E3A] text-lg"}></i>
              </div>
              <div className="text-xl font-extrabold text-gray-900">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Minimum Order Banner */}
        <div className="bg-gradient-to-r from-[#1a0a0a] via-[#2a0808] to-[#C41E3A] rounded-2xl p-6 mb-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37]/20 rounded-2xl flex-shrink-0">
              <i className="ri-vip-crown-fill text-[#D4AF37] text-2xl"></i>
            </div>
            <div>
              <div className="text-white font-extrabold text-lg">Wholesale Unlocks at $6,000 per Order</div>
              <div className="text-white/60 text-xs mt-1">
                Orders above $6,000 automatically qualify for wholesale pricing. No membership fee. No approval needed.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <div className="bg-white/10 rounded-xl px-4 py-3 text-center">
              <div className="text-[#D4AF37] font-extrabold text-lg">40%</div>
              <div className="text-white/50 text-[10px]">Bronze Savings</div>
            </div>
            <div className="bg-white/10 rounded-xl px-4 py-3 text-center">
              <div className="text-[#D4AF37] font-extrabold text-lg">50%</div>
              <div className="text-white/50 text-[10px]">Silver Savings</div>
            </div>
            <div className="bg-[#D4AF37]/20 border border-[#D4AF37]/40 rounded-xl px-4 py-3 text-center">
              <div className="text-[#D4AF37] font-extrabold text-lg">60%</div>
              <div className="text-white/50 text-[10px]">Gold Savings</div>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 bg-gray-200 rounded-full p-1 w-fit mb-8">
          {(["products", "tiers"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "products" ? "Wholesale Products" : "Pricing Tiers"}
            </button>
          ))}
        </div>

        {/* Products Tab */}
        {activeTab === "products" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wholesaleProducts.map((product) => {
              const qty = orderQty[product.id] || product.minOrderQty;
              const total = (qty * product.wholesalePrice).toFixed(2);
              const meetsMin = qty * product.wholesalePrice >= product.minOrderValue;
              return (
                <div key={product.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-[#D4AF37]/50 transition-all group">
                  {/* Image */}
                  <div className="relative w-full h-44 overflow-hidden bg-gray-50">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2 bg-[#C41E3A] text-white text-[10px] font-bold px-2 py-1 rounded-full">
                      {product.badge}
                    </div>
                    <div className="absolute top-2 right-2 bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                      Save {product.savings}%
                    </div>
                  </div>

                  <div className="p-5">
                    {/* Factory */}
                    <div className="flex items-center gap-1.5 text-[10px] text-gray-400 mb-1">
                      <span>{product.flag}</span>
                      <span>{product.factory} — {product.location}</span>
                    </div>
                    <div className="text-sm font-bold text-gray-900 leading-tight mb-3">{product.name}</div>

                    {/* Price Comparison */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1 bg-gray-50 rounded-lg p-2.5 text-center">
                        <div className="text-[10px] text-gray-400 mb-0.5">Retail Price</div>
                        <div className="text-sm font-bold text-gray-400 line-through">${product.retailPrice.toFixed(2)}</div>
                      </div>
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-gray-300"></i>
                      </div>
                      <div className="flex-1 bg-[#C41E3A]/10 rounded-lg p-2.5 text-center border border-[#C41E3A]/20">
                        <div className="text-[10px] text-[#C41E3A] font-semibold mb-0.5">Wholesale</div>
                        <div className="text-sm font-extrabold text-[#C41E3A]">${product.wholesalePrice.toFixed(2)}</div>
                      </div>
                    </div>

                    {/* Certifications */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.certifications.map((c) => (
                        <span key={c} className="text-[9px] font-semibold bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full">
                          {c}
                        </span>
                      ))}
                    </div>

                    {/* Qty Selector */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-500">Order Quantity</span>
                        <span className="text-[10px] text-gray-400">Min: {product.minOrderQty} units</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQty(product.id, qty - 10)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 cursor-pointer font-bold"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <input
                          type="number"
                          value={qty}
                          onChange={(e) => updateQty(product.id, parseInt(e.target.value) || product.minOrderQty)}
                          className="flex-1 text-center border border-gray-200 rounded-lg py-1.5 text-sm font-bold outline-none focus:border-[#C41E3A]"
                        />
                        <button
                          onClick={() => updateQty(product.id, qty + 10)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 cursor-pointer font-bold"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                    </div>

                    {/* Order Total */}
                    <div className={`rounded-lg px-3 py-2 mb-3 flex items-center justify-between ${meetsMin ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
                      <span className="text-xs font-semibold text-gray-700">Order Total:</span>
                      <div className="text-right">
                        <span className={`text-sm font-extrabold ${meetsMin ? "text-green-700" : "text-amber-700"}`}>${total}</span>
                        {!meetsMin && (
                          <div className="text-[9px] text-amber-600">Min. $6,000 required</div>
                        )}
                        {meetsMin && (
                          <div className="text-[9px] text-green-600 flex items-center gap-0.5">
                            <i className="ri-checkbox-circle-fill"></i> Wholesale unlocked!
                          </div>
                        )}
                      </div>
                    </div>

                    <Link
                      to="/wholesale"
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#C41E3A] text-white rounded-xl text-sm font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-shopping-bag-3-line"></i>
                      Request Wholesale Quote
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tiers Tab */}
        {activeTab === "tiers" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {wholesaleTiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-2xl border-2 p-7 ${tier.bgColor} ${tier.borderColor} ${tier.popular ? "scale-105" : ""}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#1a0a0a] text-[10px] font-extrabold px-4 py-1 rounded-full whitespace-nowrap">
                    MOST POPULAR
                  </div>
                )}
                <div className={`inline-flex items-center gap-2 text-sm font-extrabold mb-1 ${tier.textColor}`}>
                  <i className="ri-vip-crown-fill"></i>
                  {tier.name} Tier
                </div>
                <div className="text-2xl font-extrabold text-gray-900 mb-1">{tier.discount}</div>
                <div className="text-xs text-gray-500 mb-5">
                  Min. order: <strong>${tier.minOrder.toLocaleString()}</strong>
                  {tier.maxOrder ? ` – $${tier.maxOrder.toLocaleString()}` : "+"}
                </div>
                <ul className="space-y-2.5 mb-6">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-sm text-gray-700">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`ri-checkbox-circle-fill text-sm ${tier.textColor}`}></i>
                      </div>
                      {perk}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/wholesale"
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap bg-gradient-to-r ${tier.color} text-white hover:opacity-90`}
                >
                  <i className="ri-arrow-right-line"></i>
                  Get {tier.name} Pricing
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 bg-white rounded-2xl border border-gray-100 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#25D366]/10 rounded-2xl flex-shrink-0">
              <i className="ri-whatsapp-line text-[#25D366] text-2xl"></i>
            </div>
            <div>
              <div className="text-base font-bold text-gray-900">Ready to place a bulk order?</div>
              <div className="text-xs text-gray-500 mt-1">
                Contact our wholesale team via WhatsApp or submit a quote request. We respond within 2 hours.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <a
              href="https://wa.me/1234567890"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-xl text-sm font-bold hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i> WhatsApp Us
            </a>
            <Link
              to="/wholesale"
              className="flex items-center gap-2 bg-[#C41E3A] text-white px-5 py-3 rounded-xl text-sm font-bold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line"></i> Submit Quote Request
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WholesaleSection;
